import {rpc, wallet} from '@cityofzion/neon-core'
import {EnvHelper} from '@/helpers/EnvHelper'
import {RpcHelper} from '@/helpers/RpcHelper'
import axios from 'axios'
import {ethers} from 'ethers'
import {NeonInvoker} from '@cityofzion/neon-invoker'
import {BalanceResponse} from '@/helpers/ShrikeHelper'
import {TBridgeHistory} from '@/model/bridge/types/BridgeHistory'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'

export class NeoHelper {
  static neonInvoker: NeonInvoker | null = null
  private static neonInvokerRpc: string | null = null
  static readonly attemptsToValidateTransaction =
    EnvHelper.VUE_APP_MAX_ATTEMPTS_TO_VALIDATE_TRANSACTION ?? 25

  static async getNeonInvoker(): Promise<NeonInvoker> {
    const rpcAddress = await RpcHelper.getActiveRpc()

    if (!this.neonInvoker || this.neonInvokerRpc !== rpcAddress) {
      this.neonInvoker = await NeonInvoker.init({
        rpcAddress,
        account: new wallet.Account(),
      })
      this.neonInvokerRpc = rpcAddress
    }

    return this.neonInvoker
  }

  static async validateTransaction(
    transactionHash: string,
    rpcUrl?: string
  ): Promise<boolean> {
    const rpcClient = new rpc.RPCClient(
      rpcUrl ?? (await RpcHelper.getActiveRpc())
    )
    const maxTries = this.attemptsToValidateTransaction

    let tries = 0

    do {
      tries += 1

      await new Promise(resolve => setTimeout(resolve, 4000))

      try {
        const result = await rpcClient.getApplicationLog(transactionHash)

        const execution = result.executions[0]

        if (!execution || !execution.stack || execution.vmstate !== 'HALT')
          break

        const [stack] = execution.stack

        if (!stack || !stack.value) break

        if (execution.notifications.length <= 0) break

        return true
      } catch (error) {
        console.error(error)
      }
    } while (tries < maxTries)

    return false
  }

  static async getVoterData(address: string) {
    const {data} = await axios.post(await RpcHelper.getActiveRpc(), {
      jsonrpc: '2.0',
      id: 1,
      method: 'invokefunction',
      params: [
        '0xef4073a0f2b305a38ec4050e4d3d28bc40ea63f5',
        'getAccountState',
        [
          {
            type: 'Hash160',
            value: new wallet.Account(address).scriptHash,
          },
        ],
      ],
    })

    const candidateNeverVoted = data.result.stack[0].type === 'Any'
    if (candidateNeverVoted) {
      return ''
    }

    const voterData = data.result.stack[0].value[2]
    if (voterData.type === 'Any') {
      return ''
    }

    const candidatePubKey = voterData.value

    const candidatePubKeyHex = Buffer.from(candidatePubKey, 'base64').toString(
      'hex'
    )

    return new wallet.Account(candidatePubKeyHex).publicKey
  }

  static async getUnclaimedGasData(address: string) {
    const {data} = await axios.post(await RpcHelper.getActiveRpc(), {
      jsonrpc: '2.0',
      method: 'getunclaimedgas',
      params: [address],
      id: 1,
    })

    return data.result
  }

  static async calculateUnclaimedGasFee(address: string) {
    const neonInvoker = await this.getNeonInvoker()

    const scriptHash = NeoHelper.getScriptHashFromAddress(address)

    return neonInvoker.calculateFee({
      invocations: [
        {
          operation: 'transfer',
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          args: [
            {
              type: 'Hash160',
              value: scriptHash,
            },
            {
              type: 'Hash160',
              value: scriptHash,
            },
            {
              type: 'Integer',
              value: 0,
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
    })
  }

  static getScriptHashFromAddress(address: string) {
    return `0x${new wallet.Account(address).scriptHash}`
  }

  static isValidNeo3Address(address: string): boolean {
    const neo3Regex = /^[N][1-9A-HJ-NP-Za-km-z]{33}$/ // Base58Check, 34 characters
    return neo3Regex.test(address)
  }

  static isValidNeoXAddress(address: string): boolean {
    const neoXRegex = /^0x[a-fA-F0-9]{40}$/ // Hexadecimal Ethereum-style
    return neoXRegex.test(address)
  }

  private static async fetchNeoXIndexed<T>(url: string): Promise<T | null> {
    try {
      const {data} = await axios.get<T>(url)
      return data
    } catch (error) {
      if ((error as {response?: {status?: number}})?.response?.status === 404) {
        return null
      }

      throw error
    }
  }

  private static async callNeoXRpc(method: string, params: unknown[]) {
    const {data} = await axios.post(BridgeNetworkHelper.xRpcUrl(), {
      jsonrpc: '2.0',
      id: 1,
      method,
      params,
    })

    if (data?.error) {
      throw new Error(`${method} failed: ${data.error.message}`)
    }

    return data.result
  }

  private static formatNeoXAmount(hexAmount: string, decimals: number): string {
    const [whole, fraction = ''] = ethers.utils
      .formatUnits(hexAmount, decimals)
      .split('.')
    const trimmed = fraction.replace(/0+$/, '')

    return trimmed ? `${whole}.${trimmed}` : whole
  }

  private static async getNeoXNativeBalance(address: string): Promise<string> {
    const result = await this.callNeoXRpc('eth_getBalance', [address, 'latest'])

    return this.formatNeoXAmount(result ?? '0x0', 18)
  }

  private static async getNeoXTokenBalance(
    tokenHash: string,
    address: string,
    decimals: number
  ): Promise<string> {
    const iface = new ethers.utils.Interface([
      'function balanceOf(address owner) view returns (uint256)',
    ])

    const result = await this.callNeoXRpc('eth_call', [
      {to: tokenHash, data: iface.encodeFunctionData('balanceOf', [address])},
      'latest',
    ])

    if (!result || result === '0x') {
      return '0'
    }

    return this.formatNeoXAmount(result, decimals)
  }

  static async getNeoXBridgeBalances(
    address: string
  ): Promise<BalanceResponse[]> {
    await BridgeNetworkHelper.networkAsync()

    const gasToken = BridgeNetworkHelper.xGasToken()
    const neoToken = BridgeNetworkHelper.xNeoToken()
    const additionalToken = BridgeNetworkHelper.additionalToken()

    const erc20Tokens = [
      {symbol: 'NEO', hash: neoToken.hash, decimals: neoToken.chainDecimals},
      ...(additionalToken
        ? [
            {
              symbol: additionalToken.symbol,
              hash: additionalToken.x.hash,
              decimals: additionalToken.x.chainDecimals,
            },
          ]
        : []),
    ]

    const [nativeAmount, ...erc20Amounts] = await Promise.all([
      this.getNeoXNativeBalance(address),
      ...erc20Tokens.map(token =>
        this.getNeoXTokenBalance(token.hash, address, token.decimals)
      ),
    ])

    return [
      {
        amount: nativeAmount,
        token: {decimals: 18, hash: gasToken.hash, symbol: 'WGAS10'},
      },
      ...erc20Tokens.map((token, index) => ({
        amount: erc20Amounts[index],
        token,
      })),
    ]
  }

  static async getNeoXBalance(address: string): Promise<BalanceResponse[]> {
    await BridgeNetworkHelper.networkAsync()
    const apiUrl = BridgeNetworkHelper.xApiUrl()

    const neoXNativeToken = {
      decimals: 18,
      hash: BridgeNetworkHelper.xGasToken().hash,
      symbol: 'WGAS10',
    }

    const neoXDefaultDecimals = 18

    const balances: BalanceResponse[] = [
      {
        amount: await this.getNeoXNativeBalance(address),
        token: neoXNativeToken,
      },
    ]

    const erc20Balances = await this.fetchNeoXIndexed<any>(
      `${apiUrl}/addresses/${address}/token-balances`
    )
    if (erc20Balances && 'message' in erc20Balances) {
      throw new Error('ERC20 balance not found')
    }

    const ethBalances = erc20Balances || []
    ethBalances.forEach((balance: any) => {
      try {
        if (balance.token.type !== 'ERC-20') {
          return
        }

        const token = {
          decimals: balance.token.decimals
            ? parseInt(balance.token.decimals)
            : neoXDefaultDecimals,
          hash: balance.token.address ?? balance.token.address_hash,
          name: balance.token.symbol,
          symbol: balance.token.symbol,
        }

        balances.push({
          amount: String(Number(balance.value) / 10 ** token.decimals),
          token,
        })
      } catch {
        /* empty */
      }
    })

    return balances
  }

  static async listBridgeDepositHistory(
    skip: number = 0,
    senderAddress?: string
  ) {
    let senderScriptHash = ''

    if (senderAddress) {
      senderScriptHash = `0x${wallet.getScriptHashFromAddress(senderAddress)}`
    }

    await BridgeNetworkHelper.networkAsync()
    const BASE_URL = BridgeNetworkHelper.depositHistoryUrl()

    const {data} = await axios.post(BASE_URL, {
      jsonrpc: '2.0',
      method: 'GetBridgeDepositHistory',
      params: {
        ContractHash: BridgeNetworkHelper.n3BridgeScriptHash(),
        Sender: senderScriptHash,
        Limit: 10,
        Skip: skip,
      },
      id: 1,
    })

    const total = data.result.totalCount

    const items: TBridgeHistory[] = data.result.result.map((tx: any) => {
      return {
        from: tx.from,
        to: tx.to,
        amount: tx.amount,
        tokenSymbol: tx.symbol,
        datetime: tx.timestamp,
        txId: tx.txid,
      }
    })

    return {
      total,
      items,
    }
  }

  static async listBridgeWithdrawHistory(
    skip: number = 0,
    senderAddress?: string
  ) {
    await BridgeNetworkHelper.networkAsync()
    const BASE_URL = BridgeNetworkHelper.withdrawHistoryUrl()

    const urlFormatted = senderAddress
      ? `${BASE_URL}?limit=10&skip=${skip}&from=${senderAddress}`
      : `${BASE_URL}?limit=10&skip=${skip}`

    const {data} = await axios.get(urlFormatted)

    const items: TBridgeHistory[] = data.map((tx: any) => {
      return {
        from: tx.from,
        to: tx.to,
        amount: tx.amount,
        tokenSymbol: tx.symbol,
        datetime: tx.timestamp,
        txId: tx.txid,
      }
    })

    return {
      items,
    }
  }
}
